import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { withRouter } from 'react-router-dom';
import queryString from 'query-string';
import { Tabs, Spin } from 'antd';

import { mergeSearchParams } from '~helpers/history';
import * as actions from '~client/actions/reconciliation-list';
import Header from '~pages/reconciliation/list/header';
import Table from '~pages/reconciliation/list/table';
import Filter from '~pages/reconciliation/list/filter';
import { setItem } from '~helpers/storage';
import { communicationStatus, bookingJourneyType } from '~client/constants';
import { reconciliationOptions } from '~constants/bookings';

const mapStateToProps = state => ({
  hasManualBookings: state.auth.getIn(['properties', 'hasManualBookings']),
  hasNoPaymentHerculesBookings: state.auth.getIn(['properties', 'hasNoPaymentHerculesBookings']),
  hasNoPaymentInstantBookings: state.auth.getIn(['properties', 'hasNoPaymentInstantBookings']),
  list: state.reconciliationReducer.getIn(['list']).toJS(),
  reconciliationOption: state.auth.getIn(['base']).toJS().reconciliationOption,
});

const mapDispatchToProps = dispatch => ({
  listLandlordReconciliationOpportunities: params => {
    dispatch(actions.listLandlordReconciliationOpportunities(params));
  },
  listLandlordReconciliationOrders: params => {
    dispatch(actions.listLandlordReconciliationOrders(params));
  },
  listLandlordReconciliationOpportunitiesCount: (params, success) => {
    dispatch(actions.listLandlordReconciliationOpportunitiesCount(params, success));
  },
  getLandlordPropertyList: success => {
    dispatch(actions.getLandlordPropertyList(success));
  },
  exportListLandlordOrders: params => {
    dispatch(actions.exportListLandlordOrders(params));
  },
  exportListLandlordOpportunities: params => {
    dispatch(actions.exportListLandlordOpportunities(params));
  },
});
@connect(mapStateToProps, mapDispatchToProps)
@withRouter
@withTranslation()
export default class ReconciliationList extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      loading: true,
      filters: this.formatFilters({}),
      readyForApprovalCount: 0,
      pendingCount: 0,
      bookingJourneyTabs: [],
      dateType: this.getDefaultDateType(),
    };

    this.dateFormat = 'YYYY-MM-DD';
    this.defaultSearchParams = {
      pageNumber: 1,
      pageSize: 10,
    };
  }

  componentDidMount() {
    this.initTabs(this.props);
  }

  componentWillReceiveProps(nextProps) {
    this.initTabs(nextProps);
  }

  initTabs = props => {
    const attrs = [
      'hasNoPaymentHerculesBookings',
      'hasNoPaymentInstantBookings',
      'hasManualBookings',
    ];

    if (
      this.state.loading &&
      attrs.every(item => props[item] !== null && props[item] !== undefined)
    ) {
      const tabs = [];
      const search = queryString.parse(this.props.location.search);

      if (props.hasNoPaymentHerculesBookings) {
        tabs.push(bookingJourneyType.SEMI_AUTOMATIC);
      }

      if (props.hasNoPaymentInstantBookings) {
        tabs.push(bookingJourneyType.INSTANT_BOOK);
      }

      if (props.hasManualBookings) {
        tabs.push(bookingJourneyType.MANUAL);
      }

      this.setState(
        {
          loading: false,
          bookingJourneyTabs: tabs,
          filters: this.formatFilters({
            bookingJourney: tabs?.[0],
            ...search,
          }),
        },
        () => {
          this.initData();
        },
      );
    }
  };

  initData = () => {
    const { filters } = this.state;

    if (
      [bookingJourneyType.SEMI_AUTOMATIC, bookingJourneyType.INSTANT_BOOK].includes(
        filters.bookingJourney,
      )
    ) {
      this.props.listLandlordReconciliationOrders(filters);
    } else {
      this.props.listLandlordReconciliationOpportunities(filters);
      this.getHeaderBookingCount(filters);
    }
  };

  getDefaultDateType = () => {
    if (this.props.reconciliationOption) {
      return reconciliationOptions[this.props.reconciliationOption];
    }
    if (window.localStorage?.reconciliationOption) {
      return reconciliationOptions[window.localStorage.reconciliationOption];
    }
    return '';
  };

  getDateType = type => {
    if (reconciliationOptions[type] !== this.state.dateType) {
      const reconciliationOption = this.props.reconciliationOption || 'STUDENT_CHECK_IN';
      this.setState({
        dateType: reconciliationOptions[type || reconciliationOption],
      });
    }
  };

  getNewBookingCount = (nextLocationSearch, locationSearch) => {
    const { dateType } = this.state;
    const oldSearchDate = queryString.parse(locationSearch);
    const oldFilters = this.formatFilters(oldSearchDate);
    const newSearchDate = queryString.parse(nextLocationSearch);
    const newFilters = this.formatFilters(newSearchDate);
    const difference = oldFilters.propertyIds
      .concat(newFilters.propertyIds)
      .filter(
        item => !oldFilters.propertyIds.includes(item) || !newFilters.propertyIds.includes(item),
      );

    if (
      oldFilters[`${dateType}Start`] !== newFilters[`${dateType}Start`] ||
      oldFilters[`${dateType}End`] !== newFilters[`${dateType}End`] ||
      difference.length > 0
    ) {
      return true;
    }
    return false;
  };

  getHeaderBookingCount = filters => {
    const newParams = {};

    newParams.completedAtEnd = filters.completedAtEnd || null;
    newParams.completedAtStart = filters.completedAtStart || null;
    newParams.xbookingReferenceIds = this.formatArrayFilter(filters.xbookingReferenceIds);
    newParams.landlordApplicationIds = this.formatArrayFilter(filters.landlordApplicationIds);
    newParams.propertyIds = this.formatArrayFilter(filters.propertyIds);
    newParams.invoiceNumbers = this.formatArrayFilter(filters.invoiceNumbers);

    this.props.listLandlordReconciliationOpportunitiesCount(newParams, result => {
      if (result) {
        this.setState({
          readyForApprovalCount: result.readyForApprovalCount.totalCount,
          pendingCount: result.pendingApprovalCount.totalCount,
        });
      }
    });
  };

  pushState = data => {
    this.props.history.push(mergeSearchParams(data, this.defaultSearchParams));
  };

  setFilters = filters => {
    const value = this.formatFilters(filters);

    this.setState(
      {
        filters: value,
      },
      () => {
        this.pushState(value);
        this.initData();
      },
    );

    setItem('landlord_reconciliation_list_filters', filters);
  };

  formatFilters = filters => {
    if (
      [bookingJourneyType.SEMI_AUTOMATIC, bookingJourneyType.INSTANT_BOOK].includes(
        filters.bookingJourney,
      )
    ) {
      return {
        bookingJourney: filters.bookingJourney,
        receivableStatus: this.formatArrayFilter(filters.receivableStatus),
        propertyIds: this.formatArrayFilter(filters.propertyIds),
        orderReferenceId: filters.orderReferenceId || null,
        pageNumber: Number(filters.pageNumber) || 1,
        pageSize: Number(filters.pageSize) || 10,
        citySlug: filters.citySlug || null,
        countrySlug: filters.countrySlug || null,
        moveInDateEnd: filters.moveInDateEnd || null,
        moveInDateStart: filters.moveInDateStart || null,
        moveOutDateEnd: filters.moveOutDateEnd || null,
        moveOutDateStart: filters.moveOutDateStart || null,
        invoiceNumber: filters.invoiceNumber || null,
      };
    } else {
      return {
        bookingJourney: filters.bookingJourney,
        pageNumber: Number(filters.pageNumber) || 1,
        pageSize: Number(filters.pageSize) || 10,
        finalMoveInDateEnd: filters.finalMoveInDateEnd || null,
        finalMoveInDateStart: filters.finalMoveInDateStart || null,
        finalMoveOutDateEnd: filters.finalMoveOutDateEnd || null,
        finalMoveOutDateStart: filters.finalMoveOutDateStart || null,
        completedAtEnd: filters.completedAtEnd || null,
        completedAtStart: filters.completedAtStart || null,
        landlordApplicationIds: this.formatArrayFilter(filters.landlordApplicationIds),
        landlordBookingStatuses: this.formatArrayFilter(filters.landlordBookingStatuses),
        propertyIds: this.formatArrayFilter(filters.propertyIds),
        xbookingReferenceIds: this.formatArrayFilter(filters.xbookingReferenceIds),
        invoiceNumbers: this.formatArrayFilter(filters.invoiceNumbers),
      };
    }
  };

  formatArrayFilter = params => {
    let filter = [];

    if (params && typeof params !== 'string') {
      filter = params;
    }

    if (params && typeof params === 'string') {
      filter.push(params);
    }

    return filter;
  };

  handleTableChange = ({ current, pageSize }) => {
    const { filters } = this.state;

    const params = {};

    if (current !== filters.pageNumber) {
      params.pageNumber = current;
    }

    if (pageSize !== filters.pageSize) {
      params.pageSize = pageSize;
    }

    this.setFilters({
      ...filters,
      ...params,
    });
  };

  handleMenuTabChange = bookingJourney => {
    this.setFilters(
      this.formatFilters({
        bookingJourney,
      }),
    );
  };

  handleExport = () => {
    const { filters } = this.state;

    const params = {
      ...filters,
      pageNumber: 1,
      pageSize: 9999,
    };

    if (params.bookingJourney === bookingJourneyType.MANUAL) {
      this.props.exportListLandlordOpportunities(params);
    } else {
      this.props.exportListLandlordOrders(params);
    }
  };

  render() {
    const { loading, filters, bookingJourneyTabs, readyForApprovalCount, pendingCount } =
      this.state;
    const { t, list, history, location, getLandlordPropertyList } = this.props;

    return (
      <div className="reconciliation-list">
        <Choose>
          <When condition={loading}>
            <Spin className="reconciliation-list__loading" />
          </When>
          <Otherwise>
            <Header t={t} />

            <div className="reconciliation-list__content">
              <Tabs
                mode="horizontal"
                animated={false}
                activeKey={filters.bookingJourney}
                className="reconciliation-list__tabs"
                onChange={this.handleMenuTabChange}
              >
                <For each="tab" of={bookingJourneyTabs}>
                  <Tabs.TabPane tab={t(`properties_booking_type_${tab.toLowerCase()}`)} key={tab}>
                    <Filter
                      t={t}
                      isLoading={
                        list.communication &&
                        list.communication.status === communicationStatus.FETCHING
                      }
                      total={list.totalCount}
                      history={history}
                      location={location}
                      bookingJourney={tab}
                      filters={filters}
                      dateType={this.state.dateType}
                      readyForApprovalCount={readyForApprovalCount}
                      pendingCount={pendingCount}
                      setFilters={this.setFilters}
                      pushState={this.pushState}
                      getLandlordPropertyList={getLandlordPropertyList}
                      formatFilters={this.formatFilters}
                      handleExport={this.handleExport}
                    />

                    <div className="table-list">
                      <Table
                        list={list}
                        t={t}
                        bookingJourney={tab}
                        history={history}
                        filters={filters}
                        handleTableChange={this.handleTableChange}
                      />
                    </div>
                  </Tabs.TabPane>
                </For>
              </Tabs>
            </div>
          </Otherwise>
        </Choose>
      </div>
    );
  }
}

ReconciliationList.propTypes = {
  list: PropTypes.object,
  t: PropTypes.func,
  location: PropTypes.shape({
    search: PropTypes.string.isRequired,
  }),
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }),
  listLandlordReconciliationOpportunities: PropTypes.func,
  listLandlordReconciliationOpportunitiesCount: PropTypes.func,
  getLandlordPropertyList: PropTypes.func,
  exportListLandlordOpportunities: PropTypes.func,
  reconciliationOption: PropTypes.string,
};

ReconciliationList.defaultProps = {
  list: {},
  t: () => {},
  location: {
    search: '',
  },
  history: {
    push: () => {},
  },
  listLandlordReconciliationOpportunities: () => {},
  listLandlordReconciliationOpportunitiesCount: () => {},
  getLandlordPropertyList: () => {},
  exportListLandlordOpportunities: () => {},
  // defaultValue
  reconciliationOption: 'STUDENT_CHECK_IN',
};
